import { DocumentSettingValueType } from "../../models/documentSettings";

export const getWordFileAsBinary = async (): Promise<BlobPart> => {
  return new Promise<BlobPart>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          return reject(new Error("Failed to retrieve Word file."));
        }

        const file = result.value;
        const sliceCount = file.sliceCount;
        const slices = new Array(sliceCount);

        const getSlice = (index) => {
          if (index >= sliceCount) {

            const fullBinaryData = new Uint8Array(file.size);
            let offset = 0;
            for (const slice of slices) {
              fullBinaryData.set(slice, offset);
              offset += slice.length;
            }

            resolve(fullBinaryData);
            file.closeAsync();
            return;
          }

          file.getSliceAsync(index, (sliceResult) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
              file.closeAsync();
              return reject(sliceResult.error);
            }

            const sliceData = new Uint8Array(sliceResult.value.data);

            slices[index] = sliceData;

            getSlice(index + 1);
          });
        };

        getSlice(0);
      }
    );
  });
};

export const getBufferFromBinaryData = async (binaryData: BlobPart, binaryType: string) => {
  const fileBlob = new Blob([binaryData as BlobPart], { type: binaryType });
  const buffer = await fileBlob.arrayBuffer();
  return buffer;
};

const sanitizeText = (text: string) =>
  text
    .slice(0, 255) // Keeping only 255 characters to avoid length error using .search()
    .replace(/^"|"$/g, '') // Remove the leading and trailing quotes that wraps the text
    .replace(/'(?=[^s]|$)/g, '"') // Replace the single quotes ' for double quotes " execpt for the 's instances
    .replace(/【.*?】/g, '') // Remove text appended sometimes at the end like【4:0†uploaded-document.docx
    .trim(); // Remove any leading or trailing spaces

export const findMatchInDocument = async (text: string): Promise<boolean> => {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");

    await context.sync();

    const sanitizedTextToHighlight = sanitizeText(text);
    const fullText = body.text;
    const startOffset = fullText.indexOf(sanitizedTextToHighlight);
    if (startOffset === -1) {
      return false;
    }

    const ranges = body.search(sanitizedTextToHighlight, { matchCase: true });
    ranges.load('items');
    await context.sync();

    return ranges.items.length > 0;
  });
};

export const setDocumentSetting = async (settingName: string, settingValue: DocumentSettingValueType) => {
  return await Word.run(async (context) => {
    const settings = context.document.settings;
    settings.add(settingName, settingValue);
    await context.sync();
  });
};

export const getDocumentSetting = async (settingName: string, defaultValue = null) => {
  try {
    return await Word.run(async (context) => {
      const settings = context.document.settings;
      const setting = settings.getItem(settingName).load();
      await context.sync();
      return setting.value;
    });
  } catch (error) {
    if (error.code === 'ItemNotFound') {
      return defaultValue;
    } else {
      throw error;
    }
  }
};
